import Button, { ButtonPropsVariantOverrides } from "@mui/joy/Button";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import {
  Box,
  ColorPaletteProp,
  ListItemButton,
  Typography,
  VariantProp,
} from "@mui/joy";
import React, { useEffect, useState } from "react";
import { usePrevious } from "@uidotdev/usehooks";
import { useRecoilValue } from "recoil";
import { permanentDataAtom } from "recoil/permanentDataAtom";
import { addAlpha } from "styles/hexOpacity";
import { sendMessage } from "utils/webview";
import useFetch from "hooks/useFetch";
import { SxProps } from "@mui/joy/styles/types";
import { OverridableStringUnion } from "@mui/types";
import { theme } from "styles/theme";
import useIsInappWebview from "../../hooks/useIsInappWebview";

export default function ButtonWithModal({
  variant,
  size,
  color,
  startDecorator,
  buttonText,
  buttonTextSize,
  title,
  subtitle,
  textAlign,
  content,
  disabled,
  onClick,
  disableModalOpen,
  action,
  fetchActionUrl,
  cancelAction,
  actionText,
  cancelText,
  isListItemButton,
  onClose,
  actionButtonColor,
  actionButtonFullWidth,
  buttonFullWidth,
  actionButtonDisabled,
  clickWithEnter,
  setClickWithEnter,
  render,
  loading,
  actionAfterFetch,
  bottomIfMobile,
  isWide,
  loadingActionButton,
  sx,
}: {
  variant?: OverridableStringUnion<VariantProp, ButtonPropsVariantOverrides>;
  size?: "sm" | "md" | "lg";
  color?: "primary" | "secondary" | "danger" | "white";
  startDecorator?: React.ReactNode;
  buttonText: string;
  buttonTextSize?: "sm" | "md" | "lg";
  title?: string;
  subtitle?: string;
  textAlign?: "center";
  content?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  disableModalOpen?: boolean;
  action?: () => void;
  fetchActionUrl?: string;
  cancelAction?: () => void;
  actionText?: string;
  cancelText?: string;
  isListItemButton?: boolean;
  onClose?: () => void;
  actionButtonColor?: ColorPaletteProp;
  actionButtonFullWidth?: boolean;
  buttonFullWidth?: boolean;
  actionButtonDisabled?: boolean;
  clickWithEnter?: boolean;
  setClickWithEnter?: React.Dispatch<React.SetStateAction<boolean>>;
  render?: React.ReactNode;
  loading?: boolean;
  actionAfterFetch?: () => void;
  bottomIfMobile?: boolean;
  isWide?: boolean;
  loadingActionButton?: boolean;
  sx?: SxProps;
}) {
  const [open, setOpen] = useState(false);
  const [isActionButtonLoading, setIsActionButtonLoading] = useState(false);
  const previousOpen = usePrevious(open);
  const isInappWebview = useIsInappWebview();

  const permanantDataState = useRecoilValue(permanentDataAtom);

  const [fetchData, fetch] = useFetch({
    url: fetchActionUrl || "",
  });

  useEffect(() => {
    if (loadingActionButton !== undefined) {
      setIsActionButtonLoading(loadingActionButton);
    }
  }, [loadingActionButton]);

  useEffect(() => {
    if (fetchData) {
      setIsActionButtonLoading(false);
      setOpen(false);
      sendModalEventToApp("close");
      if (actionAfterFetch) {
        actionAfterFetch();
      }
    }
  }, [fetchData]);

  const [backdropOpacity, setBackdropOpacity] = useState(0);

  useEffect(() => {
    if (previousOpen && !open) {
      if (onClose) {
        onClose();
      }
    }
  }, [open]);

  function sendModalEventToApp(status: "open" | "close") {
    sendMessage(
      JSON.stringify({
        event: "modal",
        params: {
          status: status,
        },
      })
    );
  }

  useEffect(() => {
    return () => {
      sendModalEventToApp("close");
    };
  }, []);

  const handleClick = () => {
    if (!disableModalOpen) {
      sendModalEventToApp("open");
    }

    setTimeout(() => {
      if (onClick) {
        onClick();
      }
      if (!disableModalOpen) {
        setOpen(true);
      }
      setTimeout(() => {
        setBackdropOpacity(1);
      }, 10);
    }, 100);
  };

  useEffect(() => {
    if (clickWithEnter && !disabled) {
      handleClick();
      if (setClickWithEnter) {
        setClickWithEnter(false);
      }
    }
  }, [clickWithEnter]);

  return (
    <React.Fragment>
      {render ? (
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
        >
          {render}
        </div>
      ) : isListItemButton ? (
        <ListItemButton
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          className="flex justify-center"
        >
          <Typography>{buttonText}</Typography>
        </ListItemButton>
      ) : (
        <Button
          disabled={disabled}
          variant={variant}
          size={size}
          color={color}
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          startDecorator={startDecorator}
          loading={loading}
          sx={{
            "--joy-focus-thickness": "0px",
            "--variant-borderWidth": "0px",
            ...(buttonFullWidth && { width: "100%" }),
            ...(buttonTextSize === "sm" && { fontSize: "14px" }),
            ...sx,
          }}
        >
          {buttonText}
        </Button>
      )}
      <Modal
        open={open}
        onClose={(e, r) => {
          if (r === "backdropClick") {
            (e as MouseEvent).stopPropagation();
          }

          sendModalEventToApp("close");

          setTimeout(() => {
            setBackdropOpacity(0);
            setOpen(false);
          }, 100);
        }}
        sx={{
          "&>.MuiModal-backdrop": {
            backdropFilter: "none",
            backgroundColor: addAlpha("#000000", 0.3),
            opacity: backdropOpacity,
            transition: "all 0.3s",
          },
        }}
      >
        <ModalDialog
          aria-labelledby="nested-modal-title"
          aria-describedby="nested-modal-description"
          sx={(theme) => ({
            ...(isWide && {
              width: "100%",
              [theme.breakpoints.up("sm")]: {
                maxWidth: "600px",
              },
            }),
            ...(!isInappWebview && {
              minWidth: "480px",
            }),
            [theme.breakpoints.only("xs")]: bottomIfMobile
              ? {
                  top: "unset",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  borderRadius: 0,
                  transform: "none",
                  maxWidth: "unset",
                  paddingBottom: `${permanantDataState?.homeIndicatorHeight}px`,
                }
              : {},
            p: "32px",
            borderRadius: "24px",
            boxShadow: theme.shadow.lg,
            border: 0,
          })}
          onClick={(e) => {
            // e.preventDefault();
            e.stopPropagation();
          }}
        >
          {title && (
            <Typography
              id="nested-modal-title"
              variant={"Subtitle"}
              sx={{
                textAlign: textAlign,
                color: theme.vars.palette.Content.Strong,
                fontWeight: 700,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              id="nested-modal-description"
              variant={"Body"}
              sx={{
                textAlign: textAlign,
                color: theme.vars.palette.Content.Weak,
                fontWeight: 500,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {content}
          {actionText && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexDirection: { xs: "column", sm: "row-reverse" },
                mt: 2,
              }}
            >
              {actionText && (
                <Button
                  disabled={actionButtonDisabled}
                  variant="Solid"
                  size="md"
                  color={actionButtonColor || "primary"}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (fetchActionUrl) {
                      fetch();
                      setIsActionButtonLoading(true);
                    } else if (action) {
                      try {
                        setIsActionButtonLoading(true);
                        await action(); // action이 Promise를 반환하면 기다림
                        setOpen(false); // 성공 후 닫기
                        sendModalEventToApp("close");
                      } catch (error) {
                        console.error(error);
                        // 에러 처리 (옵션)
                      } finally {
                        setIsActionButtonLoading(false);
                      }
                    }
                  }}
                  sx={{
                    height: "46px",
                    width: actionButtonFullWidth ? "100%" : "auto",
                  }}
                  loading={isActionButtonLoading}
                >
                  {actionText}
                </Button>
              )}
              {cancelText && (
                <Button
                  variant="Plain"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (cancelAction) {
                      cancelAction();
                    }
                    setOpen(false);
                  }}
                >
                  {cancelText}
                </Button>
              )}
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </React.Fragment>
  );
}
